export type ServiceRequestType = 'New' | 'Continue' | 'Release' | 'Timeout';

export interface UssdServiceRequest {
  msisdn: string;
  serviceCode: string;
  sessionId: string;
  serviceRequestType: ServiceRequestType;
  serviceData?: string;
  operator?: string;
  operatorCountryCode?: string;
  mobileNetworkOperator?: string;
  serviceId?: string;
  transactionId?: number;
  language?: string;
  metadata?: Record<string, unknown>;
}

export interface UssdServiceResponse {
  serviceRequestType?: ServiceRequestType;
  serviceData?: string;
  sessionId?: string;
  msisdn?: string;
  operator?: string;
  operatorCountryCode?: string;
  metadata?: Record<string, unknown>;
}

export interface CountryInfo {
  code: string;
  name: string;
  flag: string;
  dialCode: string;
}

export type PhoneScreen = 'home' | 'connecting' | 'dialer' | 'ussd';

// --- USSD Application Management ---

export interface UssdRoutingConfiguration {
  id: string;
  serviceCode: string;
  serviceSubCode?: string;
  targetUrl: string;
  targetApplication?: string;
  mno?: string;
  countryCode?: string;
  version?: number;
  isActive: boolean;
  isDefault: boolean;
  createdAt?: string;
  modifiedAt?: string;
  organisationId?: string;
  passThrough?: boolean;
}

export interface UssdImpersonationContext {
  id: string;
  effectiveMsisdn: string;
  originalMsisdn: string;
  actor?: string;
  reason?: string;
  expiresAt?: string;
  strictSession?: boolean;
}

export interface UssdMsisdnImpersonation {
  id: string;
  enabled: boolean;
  sandboxOnly?: boolean;
  durationUnit?: string;
  timeToLiveDuration?: number;
  ussdImpersonationContexts?: UssdImpersonationContext[];
}

export interface UssdApplication {
  id: string;
  uuid?: string;
  name: string;
  alias?: string;
  serviceCode: string;
  organisationId: string;
  description?: string;
  enabled?: boolean;
  environment?: string;
  countryName?: string;
  countryCode?: string;
  internationalCode?: string;
  callbackUrl1?: string;
  callbackUrl2?: string;
  callbackServiceName?: string;
  callbackContentType?: string;
  callbackMethod?: string;
  callbackAuthType?: string;
  callbackBearerToken?: string;
  callbackBasicUsername?: string;
  callbackBasicPassword?: string;
  callbackApiKey?: string;
  callbackApiKeyHeader?: string;
  callbackCustomAuthHeader?: string;
  callbackCustomAuthValue?: string;
  mnoServiceDataPassThrough?: boolean;
  passthrough?: boolean;
  environmentAutoPromotion?: boolean;
  createdAt?: string;
  modifiedAt?: string;
  routingConfigurations?: UssdRoutingConfiguration[];
  ussdApplicationMsisdnImpersonations?: UssdMsisdnImpersonation[];
  ussdOrganisationProfile?: {
    id?: string;
    organisationId?: string;
    organisationName?: string;
    referenceId?: string;
    accountName?: string;
    serviceAccountId?: string;
    countryCode?: string;
    countryName?: string;
    createdAt?: string;
    modifiedAt?: string;
    status?: string;
  };
  [key: string]: unknown;
}

export interface UssdApplicationsPage {
  content: UssdApplication[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
  numberOfElements: number;
  empty: boolean;
}

export interface UssdApplicationsParams {
  page?: number;
  size?: number;
  /** Spring Pageable sort, e.g. "name,asc" or "createdAt,desc". */
  sort?: string;
}

export interface UssdOrganisationProfile {
  organisationId: string;
  organisationName: string;
  countryCode: string;
  countryName: string;
  status: 'PENDING';
  referenceId?: string;
  accountName?: string;
  serviceAccountId?: string;
}

export interface GenerateServiceCodeResponse {
  countryCode: string;
  serviceCode: string;
  allocatedCount: number;
  totalCapacity: number;
}

export interface CreateUssdApplicationRequest {
  name: string;
  alias: string;
  serviceCode?: string;
  description?: string;
  callbackUrl1: string;
  callbackMethod: string;
  callbackContentType: string;
  callbackAuthType: string;
  callbackServiceName?: string;
  countryCode: string;
  countryName: string;
  passthrough: boolean;
  mnoServiceDataPassThrough: boolean;
  environment: 'DEVELOPMENT';
  ussdOrganisationProfile: UssdOrganisationProfile;
  callbackBearerToken?: string;
  callbackBasicUsername?: string;
  callbackBasicPassword?: string;
  callbackApiKey?: string;
  callbackApiKeyHeader?: string;
  callbackCustomAuthHeader?: string;
  callbackCustomAuthValue?: string;
}

export interface PromoteUssdApplicationRequest {
  sourceEnvironment: string;
  targetEnvironment: string;
  requestedBy: string;
  promotionNotes?: string;
  serviceCode?: string;
}

export interface UssdPromotionRecord {
  id: string;
  promotionId: string;
  sourceEnvironment: string;
  targetEnvironment: string;
  version?: string;
  action?: string;
  status: string;
  requestedBy: string;
  requestedAt?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  promotedAt?: string;
  promotionNotes?: string;
}

export interface UpdateUssdApplicationRequest {
  id: string;
  name: string;
  alias: string;
  serviceCode: string;
  description?: string;
  callbackUrl1: string;
  callbackMethod: string;
  callbackContentType: string;
  callbackAuthType: string;
  callbackServiceName?: string;
  countryCode: string;
  countryName: string;
  callbackBearerToken?: string;
  callbackBasicUsername?: string;
  callbackBasicPassword?: string;
  callbackApiKey?: string;
  callbackApiKeyHeader?: string;
  callbackCustomAuthHeader?: string;
  callbackCustomAuthValue?: string;
}

/** GET /organisations/{orgId}/ussd/applications/stats */
export interface UssdApplicationStats {
  organisationId?: string;
  totalApplications: number;
  enabledApplications: number;
  disabledApplications: number;
  pendingPromotions: number;
}

/** GET /organisations/{orgId}/ussd/applications/check-duplicate */
export interface UssdDuplicateCheck {
  exists: boolean;
  serviceCodeExists: boolean;
  nameExists: boolean;
}

export type UssdUsageOutcome =
  | 'PENDING'
  | 'ACTIVE'
  | 'STARTED'
  | 'COMPLETED'
  | 'TERMINATED_BY_USER'
  | 'TERMINATED_BY_NETWORK'
  | 'TIMEOUT'
  | 'ERROR'
  | 'SESSION_FAILED';

/** GET /core/ussd-usage-records/by-organisation/{orgId}?from&to — one subscriber session. */
export interface UssdUsageRecord {
  id?: string | number;
  sessionId: string;
  applicationId?: string;
  applicationName?: string;
  serviceCode?: string;
  mno?: string;
  msisdn?: string;
  sessionStart?: string;
  sessionEnd?: string;
  durationMs?: number;
  hops?: number;
  outcome?: UssdUsageOutcome | string;
  usageStatus?: string;
  [key: string]: unknown;
}

/** A mobile network the simulator can impersonate, from /ussd/simulator/telcos/{cc}. */
export interface UssdOperator {
  /** Value sent as `operator` on simulator requests. */
  code: string;
  name: string;
}

export const COUNTRIES: CountryInfo[] = [
  // North Africa
  { code: 'DZ', name: 'Algeria', flag: '🇩🇿', dialCode: '+213' },
  { code: 'EG', name: 'Egypt', flag: '🇪🇬', dialCode: '+20' },
  { code: 'LY', name: 'Libya', flag: '🇱🇾', dialCode: '+218' },
  { code: 'MA', name: 'Morocco', flag: '🇲🇦', dialCode: '+212' },
  { code: 'SD', name: 'Sudan', flag: '🇸🇩', dialCode: '+249' },
  { code: 'TN', name: 'Tunisia', flag: '🇹🇳', dialCode: '+216' },
  // West Africa
  { code: 'BJ', name: 'Benin', flag: '🇧🇯', dialCode: '+229' },
  { code: 'BF', name: 'Burkina Faso', flag: '🇧🇫', dialCode: '+226' },
  { code: 'CV', name: 'Cape Verde', flag: '🇨🇻', dialCode: '+238' },
  { code: 'CI', name: "Côte d'Ivoire", flag: '🇨🇮', dialCode: '+225' },
  { code: 'GM', name: 'Gambia', flag: '🇬🇲', dialCode: '+220' },
  { code: 'GH', name: 'Ghana', flag: '🇬🇭', dialCode: '+233' },
  { code: 'GN', name: 'Guinea', flag: '🇬🇳', dialCode: '+224' },
  { code: 'GW', name: 'Guinea-Bissau', flag: '🇬🇼', dialCode: '+245' },
  { code: 'LR', name: 'Liberia', flag: '🇱🇷', dialCode: '+231' },
  { code: 'ML', name: 'Mali', flag: '🇲🇱', dialCode: '+223' },
  { code: 'MR', name: 'Mauritania', flag: '🇲🇷', dialCode: '+222' },
  { code: 'NE', name: 'Niger', flag: '🇳🇪', dialCode: '+227' },
  { code: 'NG', name: 'Nigeria', flag: '🇳🇬', dialCode: '+234' },
  { code: 'SN', name: 'Senegal', flag: '🇸🇳', dialCode: '+221' },
  { code: 'SL', name: 'Sierra Leone', flag: '🇸🇱', dialCode: '+232' },
  { code: 'TG', name: 'Togo', flag: '🇹🇬', dialCode: '+228' },
  // Central Africa
  { code: 'AO', name: 'Angola', flag: '🇦🇴', dialCode: '+244' },
  { code: 'CM', name: 'Cameroon', flag: '🇨🇲', dialCode: '+237' },
  { code: 'CF', name: 'Central African Republic', flag: '🇨🇫', dialCode: '+236' },
  { code: 'TD', name: 'Chad', flag: '🇹🇩', dialCode: '+235' },
  { code: 'CG', name: 'Congo', flag: '🇨🇬', dialCode: '+242' },
  { code: 'CD', name: 'DR Congo', flag: '🇨🇩', dialCode: '+243' },
  { code: 'GQ', name: 'Equatorial Guinea', flag: '🇬🇶', dialCode: '+240' },
  { code: 'GA', name: 'Gabon', flag: '🇬🇦', dialCode: '+241' },
  { code: 'ST', name: 'São Tomé and Príncipe', flag: '🇸🇹', dialCode: '+239' },
  // East Africa
  { code: 'BI', name: 'Burundi', flag: '🇧🇮', dialCode: '+257' },
  { code: 'KM', name: 'Comoros', flag: '🇰🇲', dialCode: '+269' },
  { code: 'DJ', name: 'Djibouti', flag: '🇩🇯', dialCode: '+253' },
  { code: 'ER', name: 'Eritrea', flag: '🇪🇷', dialCode: '+291' },
  { code: 'ET', name: 'Ethiopia', flag: '🇪🇹', dialCode: '+251' },
  { code: 'KE', name: 'Kenya', flag: '🇰🇪', dialCode: '+254' },
  { code: 'MG', name: 'Madagascar', flag: '🇲🇬', dialCode: '+261' },
  { code: 'MU', name: 'Mauritius', flag: '🇲🇺', dialCode: '+230' },
  { code: 'MZ', name: 'Mozambique', flag: '🇲🇿', dialCode: '+258' },
  { code: 'RW', name: 'Rwanda', flag: '🇷🇼', dialCode: '+250' },
  { code: 'SC', name: 'Seychelles', flag: '🇸🇨', dialCode: '+248' },
  { code: 'SO', name: 'Somalia', flag: '🇸🇴', dialCode: '+252' },
  { code: 'SS', name: 'South Sudan', flag: '🇸🇸', dialCode: '+211' },
  { code: 'TZ', name: 'Tanzania', flag: '🇹🇿', dialCode: '+255' },
  { code: 'UG', name: 'Uganda', flag: '🇺🇬', dialCode: '+256' },
  // Southern Africa
  { code: 'BW', name: 'Botswana', flag: '🇧🇼', dialCode: '+267' },
  { code: 'SZ', name: 'Eswatini', flag: '🇸🇿', dialCode: '+268' },
  { code: 'LS', name: 'Lesotho', flag: '🇱🇸', dialCode: '+266' },
  { code: 'MW', name: 'Malawi', flag: '🇲🇼', dialCode: '+265' },
  { code: 'NA', name: 'Namibia', flag: '🇳🇦', dialCode: '+264' },
  { code: 'ZA', name: 'South Africa', flag: '🇿🇦', dialCode: '+27' },
  { code: 'ZM', name: 'Zambia', flag: '🇿🇲', dialCode: '+260' },
  { code: 'ZW', name: 'Zimbabwe', flag: '🇿🇼', dialCode: '+263' },
  // Island Nations
  { code: 'RE', name: 'Réunion', flag: '🇷🇪', dialCode: '+262' },
  { code: 'YT', name: 'Mayotte', flag: '🇾🇹', dialCode: '+262' },
];
